import { isoDateStringToLocalFormat } from 'ComponentLibrary'
import type { CustomerExistingValuation as ExistingValuation } from '@einwert/valuation-api-client'
import type { AssetStructure, ExistingAsset } from '@einwert/valuation-api-client'
import { MissingDataCellMeta } from 'ClientPortal/src/pages/Assets/AssetList/ExportMissingData/types'
import type { TFunction } from 'i18next'
import { identity, isEmpty, isNil } from 'lodash'

export const STARTING_COLUMN_INDEX = 1
export const STARTING_ROW_INDEX = 1

export type MissingDataExportCopy = {
    featureLabel: string
    fileNamePrefix: string
    modalPrimaryActionLabel: string
    modalDescription: string
    modalDownloadInfo: string
    sectionTitleAsset: string
    sectionTitleStructures: string
    sectionTitleValuations: string
    emptyStateNoAsset: string
    emptyStateNoStructures: string
    emptyStateNoValuations: string
    legendTitle: string
    legendMeaningAvailable: string
    legendMeaningMissing: string
    legendMeaningMissingOptional: string
    assetCells: MissingDataCellMeta<ExistingAsset>[]
    structureCells: MissingDataCellMeta<AssetStructure>[]
    valuationCells: MissingDataCellMeta<ExistingValuation>[]
}

export function getMissingDataExportCopy({
    tCommon,
    tExport,
    locale = 'de-DE',
}: {
    tCommon: TFunction
    tExport: TFunction
    locale?: string
}): MissingDataExportCopy {
    const formatDate = (value: string) => isoDateStringToLocalFormat(value, false, locale)

    return {
        featureLabel: tExport('featureLabel'),
        fileNamePrefix: tExport('fileNamePrefix'),
        modalPrimaryActionLabel: tExport('modalPrimaryActionLabel'),
        modalDescription: tExport('modalDescription'),
        modalDownloadInfo: tExport('modalDownloadInfo'),
        sectionTitleAsset: tCommon('asset'),
        sectionTitleStructures: tExport('sectionTitleStructures'),
        sectionTitleValuations: tExport('sectionTitleValuations'),
        emptyStateNoAsset: tExport('emptyStateNoAsset'),
        emptyStateNoStructures: tExport('emptyStateNoStructures'),
        emptyStateNoValuations: tExport('emptyStateNoValuations'),
        legendTitle: tExport('legendTitle'),
        legendMeaningAvailable: tExport('legendMeaningAvailable'),
        legendMeaningMissing: tExport('legendMeaningMissing'),
        legendMeaningMissingOptional: tExport('legendMeaningMissingOptional'),
        assetCells: [
            {
                header: tExport('assetColumnCustomerAssetReference'),
                paths: ['customer_asset_reference'],
                format: identity,
                greyedOut: true,
            },
            { header: tExport('assetColumnStreet'), paths: ['address.street'], format: identity },
            { header: tExport('assetColumnHouseNumber'), paths: ['address.house_number'], format: identity },
            { header: tExport('assetColumnZipCode'), paths: ['address.zip'], format: identity },
            { header: tExport('assetColumnCity'), paths: ['address.city'], format: identity },
            { header: tExport('assetColumnRegion'), paths: ['address.state'], format: identity },
            { header: tExport('assetColumnCountry'), paths: ['address.country'], format: identity },
            { header: tExport('assetColumnCoordinates'), paths: ['address.coordinates'] },
            { header: tExport('assetColumnTitleImage'), paths: ['title_image'] },
        ],
        structureCells: [
            { header: tExport('structureColumnTitle'), paths: ['title'], format: identity },
            { header: tExport('structureColumnUsageType'), paths: ['usage_type'] },
            { header: tExport('structureColumnConstructionYear'), paths: ['construction_year'] },
            { header: tExport('structureColumnEnergyEfficiencyClass'), paths: ['energy_certificate_details.energy_efficiency_class'] },
            { header: tExport('structureColumnPrimaryPowerConsumption'), paths: ['energy_certificate_details.primary_power_consumption'] },
            { header: tExport('structureColumnHeatingEnergySource'), paths: ['energy_certificate_details.heating_energy_source'] },
            { header: tExport('structureColumnHotWaterEnergySource'), paths: ['energy_certificate_details.hot_water_energy_source'] },
            { header: tExport('structureColumnUsageArea'), paths: ['energy_certificate_details.usage_area'] },
        ],
        valuationCells: [
            { header: tExport('valuationColumnTitle'), paths: ['title'], format: identity },
            { header: tExport('valuationColumnValuationDate'), paths: ['valuation_date'], format: formatDate },
            { header: tExport('valuationColumnAppraiser'), paths: ['appraiser_assignment.main'] },
            {
                header: tExport('valuationColumnAppraisingEntityName'),
                paths: ['appraising_entity_name'],
                format: identity,
                greyedOut: true,
            },
            { header: tCommon('marketValue'), paths: ['market_value.amount'] },
            { header: tExport('valuationColumnPdf'), paths: ['report_file'] },
            { header: tExport('valuationColumnMarketData'), paths: ['comparables.market_data'], greyedOut: true },
            { header: tExport('valuationColumnQualityAssessmentDate'), paths: ['quality_assessment_date'], format: formatDate },
            { header: tExport('valuationColumnReportDate'), paths: ['report_date'], format: formatDate },
            { header: tExport('valuationColumnInspectionDate'), paths: ['inspection_date'], format: formatDate },
            { header: tExport('valuationColumnInspector'), paths: ['inspection.primary_inspector'] },
            { header: tExport('valuationColumnSensitivityMatrix'), paths: ['income_approach_sensitivity_matrix'] },
            { header: tExport('valuationColumnTransactionCosts'), paths: ['transaction_costs'], greyedOut: true },
            { header: tExport('valuationColumnLandArea'), paths: ['land_area'] },
            { header: tExport('valuationColumnGrossFloorArea'), paths: ['gross_floor_area'] },
            { header: tExport('valuationColumnUsageArea'), paths: ['usage_area'], greyedOut: true },
            { header: tExport('valuationColumnLivingArea'), paths: ['living_area'], greyedOut: true },
            { header: tCommon('grossYield'), paths: ['gross_yield'], zeroIsEmpty: false },
            { header: tExport('valuationColumnLandValue'), paths: ['land_value'] },
            { header: tExport('valuationColumnIncomeMarketValue'), paths: ['income_market_value'] },
            { header: tExport('valuationColumnCostMarketValue'), paths: ['cost_market_value'], greyedOut: true },
            { header: tExport('valuationColumnComparisonMarketValue'), paths: ['comparison_market_value'], greyedOut: true },
            { header: tExport('valuationColumnDiscountedCashflow'), paths: ['dcf'], greyedOut: true },
            { header: tExport('valuationColumnGrossYieldMultiplier'), paths: ['gross_yield_multiplier'], zeroIsEmpty: false },
            { header: tCommon('grossInitialYield'), paths: ['gross_initial_yield'], zeroIsEmpty: false },
            { header: tExport('valuationColumnNetYieldMultiplier'), paths: ['net_yield_multiplier'], zeroIsEmpty: false },
            { header: tCommon('wault'), paths: ['wault'] },
            {
                header: tExport('valuationColumnTenantIncomeBreakdown'),
                paths: [
                    'tenant_summary.residential',
                    'tenant_summary.office',
                    'tenant_summary.retail',
                    'tenant_summary.logistics',
                    'tenant_summary.parking',
                    'tenant_summary.other',
                    'tenant_summary.commercial',
                    'tenant_summary.gastronomy',
                    'tenant_summary.industry',
                    'tenant_summary.hotel',
                    'tenant_summary.storage',
                    'tenant_summary.underground_parking',
                    'tenant_summary.leisure',
                    'tenant_summary.social',
                    'tenant_summary.archive',
                    'tenant_summary.outdoor_parking',
                    'tenant_summary.other_parking',
                    'tenant_summary.garage_parking',
                    'tenant_summary.other_use',
                    'tenant_summary.open_space',
                ],
            },
            { header: tCommon('contractRent'), paths: ['tenant_summary.total.actual_rental_income.total.amount'] },
            { header: tExport('valuationColumnYearlyGrossRentalIncome'), paths: ['tenant_summary.total.gross_rental_income.total.amount'] },
            { header: tExport('valuationColumnYearlyNetRentalIncome'), paths: ['tenant_summary.total.net_rental_income.total.amount'] },
            { header: tExport('valuationColumnMaintenanceCosts'), paths: ['tenant_summary.total.maintenance_cost.total.amount'] },
            { header: tExport('valuationColumnAdministrativeCosts'), paths: ['tenant_summary.total.administrative_cost.total.amount'] },
            { header: tExport('valuationColumnOperatingCosts'), paths: ['tenant_summary.total.operating_cost.total.amount'] },
            { header: tExport('valuationColumnLossOfRentRiskCosts'), paths: ['tenant_summary.total.loss_of_rent_risk_cost.total.amount'] },
            { header: tExport('valuationColumnManagementCosts'), paths: ['tenant_summary.total.management_cost.total.amount'] },
            { header: tCommon('discountRate'), paths: ['discount_rate'] },
        ],
    }
}

export function checkIfEmpty(value: unknown, zeroIsEmpty = true): boolean {
    if (isNil(value)) {
        return true
    }
    if (typeof value === 'string' && value.trim() === '') {
        return true
    }
    if (zeroIsEmpty && value === 0) {
        return true
    }
    if (Array.isArray(value) && isEmpty(value)) {
        return true
    }
    return typeof value === 'object' && !Array.isArray(value) && isEmpty(Object.keys(value))
}
